import './RunSelectorDropdown.less';

// eslint-disable-next-line wandb/no-deprecated-imports
import {IconButton} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {TruncateByCharsWithTooltip} from '@wandb/weave/components/TruncateByCharsWithTooltip';
import React from 'react';
import {
  components,
  ControlProps,
  DropdownIndicatorProps,
  MenuListProps,
  MenuProps,
  ValueContainerProps,
} from 'react-select';

import {RunWithHistoryAndMediaWandb} from '../../MediaCard';

type RunSelectorDropdownProps = {
  selectedRun: RunWithHistoryAndMediaWandb | null;
  onRunSelect?: (run: RunWithHistoryAndMediaWandb) => void;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
};

type RunOption = {
  value: string;
  label: string;
  run: RunWithHistoryAndMediaWandb;
};

export const RunSelectorDropdown = ({
  selectedRun,
  onRunSelect,
  runsWithMedia,
}: RunSelectorDropdownProps) => {
  const options: RunOption[] = runsWithMedia.map(run => ({
    value: run.name,
    label: run.displayName ?? run.name,
    run,
  }));

  const selectedOption = selectedRun
    ? options.find(option => option.value === selectedRun.name)
    : null;

  const handleChange = (option: RunOption | null) => {
    if (option && onRunSelect) {
      onRunSelect(option.run);
    }
  };

  // Custom Option component that uses TruncateByCharsWithTooltip
  const CustomOption = (props: any) => (
    <components.Option {...props}>
      <TruncateByCharsWithTooltip
        text={props.data.label}
        maxChars={30}
        truncatedPart="middle"
        Wrapper={null}>
        {({truncatedText}) => <span>{truncatedText}</span>}
      </TruncateByCharsWithTooltip>
    </components.Option>
  );

  return (
    <Select<RunOption, false>
      className="run-selector-dropdown max-w-[500px] text-moon-600"
      value={selectedOption}
      onChange={handleChange}
      options={options}
      placeholder="Select Run"
      size="medium"
      isSearchable={true}
      isClearable={false}
      components={{
        Control: (props: ControlProps<RunOption, false>) => (
          <components.Control
            {...props}
            className="h-full text-lg/[32px] text-moon-600 font-semibold"
          />
        ),
        Option: CustomOption,
        ValueContainer: (props: ValueContainerProps<RunOption, false>) => (
          <components.ValueContainer
            {...props}
            className="p-0 text-lg/[32px] text-moon-600 font-semibold"
          />
        ),
        Menu: (props: MenuProps<RunOption, false>) => (
          <components.Menu {...props} className="z-10" />
        ),
        MenuList: (props: MenuListProps<RunOption, false>) => (
          <components.MenuList {...props} className="z-10" />
        ),
        DropdownIndicator: (
          props: DropdownIndicatorProps<RunOption, false>
        ) => (
          <components.DropdownIndicator {...props}>
            <IconButton
              variant="ghost"
              onClick={
                props.selectProps.menuIsOpen
                  ? props.selectProps.onMenuClose
                  : props.selectProps.onMenuOpen
              }
              icon={
                props.selectProps.menuIsOpen ? 'chevron-up' : 'chevron-down'
              }
              aria-label={
                props.selectProps.menuIsOpen
                  ? 'Close run selector dropdown'
                  : 'Open run selector dropdown'
              }
            />
          </components.DropdownIndicator>
        ),
      }}
    />
  );
};
